import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { Colors, Shadows } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { DurationInput } from '@/components/route-planner/DurationInput';
import { PassengerChips } from '@/components/route-planner/PassengerChips';
import { TRANSPORT_MODES, getTransportModeConfig } from '@/types/transport';
import type { TransportMode } from '@/types/transport';
import type { FamilyMember } from '@/types/family-member';
import type { RouteLeg, RoutePlanStop, RouteStayLeg, RouteTravelLeg } from '@/types/route-plan';

const PASTEL_STAY_BORDER = '#A8D8EA';
const FLEXIBLE_TODO_BORDER = '#9B5DE5';

type RouteLegCardProps = {
  leg: RouteLeg;
  stop?: RoutePlanStop;              // stay legs: the stop this leg is at
  inlineTodoTitles?: string[];       // travel legs: attached no-location to-dos
  familyMembers: FamilyMember[];
  expanded?: boolean;                // travel legs: mode selector open
  onPressTravel?: (legId: string) => void;
  onSetDuration?: (legId: string, minutes: number) => void;
  onChangeMode?: (legId: string, mode: TransportMode) => void;
  onDeleteLeg?: (legId: string) => void;
};

/**
 * RouteLegCard — one entry in the review timeline: a stay card (white card,
 * pastel left border; dashed purple for flexible to-dos) or an indented
 * travel row (dotted connector, transport icon, duration stepper).
 */
export function RouteLegCard({
  leg,
  stop,
  inlineTodoTitles = [],
  familyMembers,
  expanded = false,
  onPressTravel,
  onSetDuration,
  onChangeMode,
  onDeleteLeg,
}: RouteLegCardProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  if (leg.kind === 'stay') {
    return (
      <StayLegCard
        leg={leg}
        stop={stop}
        familyMembers={familyMembers}
        palette={palette}
        onDeleteLeg={onDeleteLeg}
      />
    );
  }

  return (
    <TravelLegRow
      leg={leg}
      inlineTodoTitles={inlineTodoTitles}
      palette={palette}
      expanded={expanded}
      onPress={onPressTravel}
      onSetDuration={onSetDuration}
      onChangeMode={onChangeMode}
      onDeleteLeg={onDeleteLeg}
    />
  );
}

type Palette = (typeof Colors)['light'];

function StayLegCard({
  leg,
  stop,
  familyMembers,
  palette,
  onDeleteLeg,
}: {
  leg: RouteStayLeg;
  stop?: RoutePlanStop;
  familyMembers: FamilyMember[];
  palette: Palette;
  onDeleteLeg?: (legId: string) => void;
}) {
  const isFlexibleTodo = stop?.source === 'todo';
  // Home stops anchor the route's start/end and cannot be deleted.
  const canDelete = Boolean(onDeleteLeg) && stop?.source !== 'home';

  return (
    <View
      style={[
        styles.stayCard,
        {
          backgroundColor: palette.cardBackground,
          borderLeftColor: isFlexibleTodo ? FLEXIBLE_TODO_BORDER : PASTEL_STAY_BORDER,
        },
        isFlexibleTodo && styles.stayCardFlexible,
      ]}
    >
      <View style={styles.stayHeader}>
        <Text style={[styles.stayTitle, { color: palette.text, fontFamily: getBodyFont() }]}>
          {stop?.title ?? 'Stop'}
        </Text>
        <View style={styles.stayHeaderRight}>
          <Text style={[styles.stayTimes, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
            {leg.arriveTime === leg.departTime
              ? leg.arriveTime
              : `${leg.arriveTime} – ${leg.departTime}`}
          </Text>
          {canDelete && (
            <TouchableOpacity onPress={() => onDeleteLeg?.(leg.id)} hitSlop={8}>
              <Ionicons name="close" size={16} color={palette.textMuted} />
            </TouchableOpacity>
          )}
        </View>
      </View>
      {stop?.location ? (
        <Text style={[styles.stayLocation, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          {stop.location}
        </Text>
      ) : null}
      <PassengerChips passengerChanges={leg.passengerChanges} familyMembers={familyMembers} />
      {leg.warning && (
        <View style={[styles.warningChip, { backgroundColor: palette.warning + '33' }]}>
          <Ionicons name="alert-circle" size={14} color={palette.warning} />
          <Text style={[styles.warningText, { color: palette.text, fontFamily: getBodyFont() }]}>
            {leg.warning}
          </Text>
        </View>
      )}
    </View>
  );
}

function TravelLegRow({
  leg,
  inlineTodoTitles,
  palette,
  expanded,
  onPress,
  onSetDuration,
  onChangeMode,
  onDeleteLeg,
}: {
  leg: RouteTravelLeg;
  inlineTodoTitles: string[];
  palette: Palette;
  expanded: boolean;
  onPress?: (legId: string) => void;
  onSetDuration?: (legId: string, minutes: number) => void;
  onChangeMode?: (legId: string, mode: TransportMode) => void;
  onDeleteLeg?: (legId: string) => void;
}) {
  const modeConfig = getTransportModeConfig(leg.mode);

  return (
    <View style={styles.travelWrapper}>
      <View style={[styles.travelConnector, { borderColor: palette.borderMuted }]} />
      <View style={styles.travelBody}>
        <TouchableOpacity
          style={styles.travelRow}
          onPress={() => onPress?.(leg.id)}
          activeOpacity={0.7}
        >
          <Ionicons
            name={modeConfig.icon as keyof typeof Ionicons.glyphMap}
            size={18}
            color={palette.primary}
          />
          <Text style={[styles.travelDepart, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
            {leg.departTime}
          </Text>
          <DurationInput
            durationMinutes={leg.durationMinutes}
            onCommit={(minutes) => onSetDuration?.(leg.id, minutes)}
          />
          {leg.note ? (
            <Text
              style={[styles.travelNote, { color: palette.textMuted, fontFamily: getBodyFont() }]}
              numberOfLines={1}
            >
              {leg.note}
            </Text>
          ) : null}
          <View style={styles.travelRowSpacer} />
          {onDeleteLeg && (
            <TouchableOpacity onPress={() => onDeleteLeg(leg.id)} hitSlop={8}>
              <Ionicons name="close" size={16} color={palette.textMuted} />
            </TouchableOpacity>
          )}
        </TouchableOpacity>

        {inlineTodoTitles.map((title) => (
          <Text
            key={title}
            style={[styles.inlineTodo, { color: palette.textMuted, fontFamily: getBodyFont() }]}
          >
            {title} — during {modeConfig.label.toLowerCase()} ride
          </Text>
        ))}

        {expanded && (
          <View style={styles.modeRow}>
            {TRANSPORT_MODES.map((config) => {
              const isActive = config.mode === leg.mode;
              return (
                <TouchableOpacity
                  key={config.mode}
                  style={[
                    styles.modeButton,
                    Shadows.sm,
                    {
                      backgroundColor: isActive ? palette.primary : palette.cardBackground,
                      borderColor: isActive ? palette.primary : palette.border,
                    },
                  ]}
                  onPress={() => onChangeMode?.(leg.id, config.mode)}
                >
                  <Ionicons
                    name={config.icon as keyof typeof Ionicons.glyphMap}
                    size={16}
                    color={isActive ? '#fff' : palette.textMuted}
                  />
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  stayCard: {
    borderRadius: BorderRadius.md,
    borderLeftWidth: 4,
    padding: Spacing.md,
    gap: Spacing.xs,
  },
  stayCardFlexible: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: FLEXIBLE_TODO_BORDER,
    borderLeftWidth: 4,
  },
  stayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.sm,
  },
  stayHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  stayTitle: {
    fontSize: 15,
    fontWeight: '600',
    flexShrink: 1,
  },
  stayTimes: {
    fontSize: 13,
  },
  stayLocation: {
    fontSize: 12,
  },
  warningChip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: Spacing.xs,
    paddingHorizontal: Spacing.sm,
    paddingVertical: 2,
    borderRadius: BorderRadius.sm,
  },
  warningText: {
    fontSize: 12,
    fontWeight: '600',
  },
  travelWrapper: {
    flexDirection: 'row',
    paddingLeft: Spacing.lg,
  },
  travelConnector: {
    borderLeftWidth: 2,
    borderStyle: 'dotted',
    marginRight: Spacing.md,
  },
  travelBody: {
    flex: 1,
    paddingVertical: Spacing.sm,
    gap: Spacing.xs,
  },
  travelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  travelDepart: {
    fontSize: 13,
  },
  travelNote: {
    fontSize: 12,
    flexShrink: 1,
  },
  travelRowSpacer: {
    flexGrow: 1,
  },
  inlineTodo: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  modeRow: {
    flexDirection: 'row',
    gap: Spacing.sm,
    paddingTop: Spacing.xs,
  },
  modeButton: {
    width: 40,
    height: 36,
    borderRadius: BorderRadius.sm,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
